// Copyright 2025 saucey, inc
import React, { useEffect, useState, useCallback } from "react";
import _ from "lodash";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import Loader from "../../components/Loader.jsx";
import {
  getSupportedAPIs,
  createRetailerAPICredentials,
  updateRetailerAPICredentials,
  deleteRetailerAPICredentials,
  testRetailerAPICredentials,
  testRetailerAPICredentialsRaw,
  getLightspeedShops,
  getLightspeedRetailMasterTokens,
} from "../../api/marketplace.js";

const IGNORE_FIELDS = [
  "_id",
  "account",
  "provider",
  "__v",
  "master",
  "is_active",
  "required_fields",
  "provide_oauth_link",
  "instructions",
  "access_token",
  "refresh_token",
  "account_id",
  "last_updated",
];

function formatLabel(key) {
  return key
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getData(response) {
  // The marketplace API helpers unwrap the axios envelope, but tolerate raw
  // responses if the helper changes in the future.
  return response?.data ?? response;
}

function MarketplaceAPIDialog({ seller, onComplete, onCancel, onTestResults }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [lightspeedShops, setLightspeedShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [loadingShops, setLoadingShops] = useState(false);
  const [masterTokens, setMasterTokens] = useState([]);
  const [loadingMasterTokens, setLoadingMasterTokens] = useState(false);
  const [selectedMasterToken, setSelectedMasterToken] = useState(null);

  const handleApiError = useCallback((err, action) => {
    console.error(`Error ${action}:`, err);
    if (err?.response?.status === 500 && err?.response?.data) {
      setError(
        typeof err.response.data === "object"
          ? JSON.stringify(err.response.data, null, 2)
          : err.response.data
      );
    } else {
      setError(
        err?.response?.data?.message || `An error occurred while ${action}`
      );
    }
  }, []);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await getSupportedAPIs();
      const data = getData(results);
      if (data && data.length) {
        let selected = null;
        let currentCredentials = seller?.api_credentials || {};
        if (seller?.api_credentials?.provider) {
          selected = data.find(
            (p) => p.provider === seller.api_credentials.provider
          );
          if (selected) {
            currentCredentials = _.cloneDeep(seller.api_credentials);
          }
        }
        setProviders(data);
        setSelectedProvider(selected);
        setCredentials(currentCredentials);
      }
    } catch (err) {
      handleApiError(err, "loading API providers");
    } finally {
      setLoading(false);
    }
  }, [seller, handleApiError]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const updateCredential = useCallback((path, value) => {
    setCredentials((prev) => {
      const next = _.cloneDeep(prev);
      _.set(next, path, value);
      return next;
    });
  }, []);

  const handleProviderChange = useCallback(
    (providerName) => {
      const provider = providers.find((p) => p.provider === providerName);
      if (!provider) return;
      if (selectedProvider?.provider !== provider.provider) {
        setCredentials(_.cloneDeep(provider));
        setLightspeedShops([]);
        setSelectedShop(null);
        setMasterTokens([]);
        setSelectedMasterToken(null);
      }
      setSelectedProvider(provider);
    },
    [providers, selectedProvider]
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedProvider || !seller?.store_id) return;
    setLoading(true);
    setError(null);
    try {
      const body = {
        fields: credentials,
        provider: selectedProvider.provider,
      };
      if (_.isEmpty(seller?.api_credentials)) {
        await createRetailerAPICredentials(seller.store_id, body);
      } else {
        await updateRetailerAPICredentials(seller.store_id, body);
      }
      if (onComplete) onComplete?.();
    } catch (err) {
      handleApiError(err, "saving credentials");
    } finally {
      setLoading(false);
    }
  }, [selectedProvider, seller, credentials, onComplete, handleApiError]);

  const handleTest = useCallback(async () => {
    if (!selectedProvider) {
      setError("Please select an API provider first");
      return;
    }
    if (!seller?.store_id) return;
    let creds = _.isEmpty(credentials) ? seller?.api_credentials : credentials;
    if (selectedProvider.required_fields?.length > 0) {
      const missing = selectedProvider.required_fields.filter(
        (field) => !creds[field] || String(creds[field]).trim() === ""
      );
      if (missing.length > 0) {
        setError(
          `Please fill in all required fields: ${missing
            .map(formatLabel)
            .join(", ")}`
        );
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      const results = await testRetailerAPICredentialsRaw(seller.store_id, creds);
      const data = getData(results);
      const filename = `${seller.store_id}_api_test_results.csv`;

      const deliverCsv = (blobOrData) => {
        const blob =
          blobOrData instanceof Blob
            ? blobOrData
            : new Blob([blobOrData], { type: "text/csv" });
        const file = new File([blob], filename, { type: "text/csv" });
        if (onTestResults) onTestResults(file);
        setSuccessMessage(
          "API credentials tested successfully. Menu CSV is ready to submit."
        );
        setTimeout(() => setSuccessMessage(null), 5000);
      };

      // if URL is present it's an S3 URL — fetch into a File for the parent
      if (data?.url) {
        try {
          const res = await fetch(data.url);
          if (!res.ok) throw new Error("Failed to fetch test results");
          deliverCsv(await res.blob());
        } catch (fetchErr) {
          // CORS or fetch failure — fall back to opening the URL
          console.warn("Could not fetch test CSV for FileUpload:", fetchErr);
          const link = document.createElement("a");
          link.href = data.url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setSuccessMessage(
            "API credentials tested successfully. Results downloaded."
          );
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      } else {
        // raw CSV body — hand a File up to the parent FileUpload
        const headers = results.headers;
        const contentType = headers["content-type"] || "";
        if (contentType.includes("application/octet-stream")) {
          deliverCsv(results.data);
        } else {
          setError("No results returned from API test");
          setTimeout(() => setError(null), 5000);
        }
      }
    } catch (err) {
      handleApiError(err, "testing credentials");
    } finally {
      setLoading(false);
    }
  }, [
    selectedProvider,
    seller,
    credentials,
    handleApiError,
    onTestResults,
  ]);

  const handleRemove = useCallback(async () => {
    if (!seller?.store_id) return;
    setLoading(true);
    setError(null);
    try {
      await deleteRetailerAPICredentials(seller.store_id);
      if (onComplete) onComplete?.();
    } catch (err) {
      handleApiError(err, "removing credentials");
    } finally {
      setLoading(false);
    }
  }, [seller, onComplete, handleApiError]);

  const fetchMasterTokens = useCallback(async () => {
    setLoadingMasterTokens(true);
    setError(null);
    try {
      const response = await getLightspeedRetailMasterTokens();
      let tokens = getData(response);
      if (!Array.isArray(tokens)) tokens = [tokens];
      setMasterTokens(tokens);
    } catch (err) {
      handleApiError(err, "fetching master tokens");
    } finally {
      setLoadingMasterTokens(false);
    }
  }, [handleApiError]);

  const fetchShops = useCallback(async () => {
    if (!seller?.store_id) return;
    setLoadingShops(true);
    setError(null);
    try {
      const response = await getLightspeedShops(seller.store_id, credentials);
      let shops = getData(response);
      if (!Array.isArray(shops)) shops = [shops];
      setLightspeedShops(shops);
    } catch (err) {
      handleApiError(err, "fetching shops");
    } finally {
      setLoadingShops(false);
    }
  }, [seller, credentials, handleApiError]);

  const handleShopSelect = useCallback(
    (shopId) => {
      const shop = lightspeedShops.find((s) => s.shopID === shopId);
      setSelectedShop(shop);
      updateCredential("item_shop_id", shopId);
    },
    [lightspeedShops, updateCredential]
  );

  const handleMasterTokenSelect = useCallback(
    (accountId) => {
      const token = masterTokens.find((t) => t.account_id === accountId);
      setSelectedMasterToken(token);
      updateCredential("account_id", accountId);
    },
    [masterTokens, updateCredential]
  );

  const renderCredentials = useCallback(
    (obj, pathPrefix = "") => {
      const requiredFields = selectedProvider?.required_fields || [];
      return Object.keys(obj).map((key) => {
        if (IGNORE_FIELDS.includes(key)) return null;
        const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key;
        const value = obj[key];
        const formattedLabel = formatLabel(key);
        const isRequired = requiredFields.includes(key);

        if (_.isString(value)) {
          let displayValue = value;
          if (displayValue.includes("{store_id}")) {
            displayValue = displayValue.replace("{store_id}", seller?.store_id || "");
          }
          return (
            <div key={currentPath} className="col-span-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                {formattedLabel}
                {isRequired && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type="text"
                className="form-input w-full"
                value={displayValue}
                onChange={(e) => updateCredential(currentPath, e.target.value)}
              />
            </div>
          );
        }

        if (_.isBoolean(value)) {
          return (
            <div key={currentPath} className="col-span-1 flex items-center">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={value}
                  onChange={(e) =>
                    updateCredential(currentPath, e.target.checked)
                  }
                />
                {formattedLabel}
              </label>
            </div>
          );
        }

        if (_.isNumber(value)) {
          return (
            <div key={currentPath} className="col-span-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                {formattedLabel}
                {isRequired && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type="number"
                className="form-input w-full"
                value={value}
                onChange={(e) =>
                  updateCredential(
                    currentPath,
                    e.target.value === "" ? "" : parseFloat(e.target.value)
                  )
                }
              />
            </div>
          );
        }

        if (_.isObject(value) && value !== null) {
          return (
            <div key={currentPath} className="col-span-full">
              <h4 className="text-base font-medium text-slate-800 dark:text-slate-100 mt-4 mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
                {formattedLabel}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderCredentials(value, currentPath)}
              </div>
            </div>
          );
        }

        return null;
      });
    },
    [selectedProvider, seller, updateCredential]
  );

  if (loading && !providers.length) {
    return (
      <div className="py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/20 p-4">
          <div className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">
            {error}
          </div>
          <div className="mt-3">
            <SecondaryButton onClick={() => setError(null)}>
              Dismiss
            </SecondaryButton>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="rounded border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-4">
          <div className="text-sm text-emerald-700 dark:text-emerald-300">
            {successMessage}
          </div>
          <div className="mt-3">
            <SecondaryButton onClick={() => setSuccessMessage(null)}>
              Dismiss
            </SecondaryButton>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
          API Configuration
          {seller?.store_id && (
            <span className="block text-sm font-normal text-slate-500 dark:text-slate-400 mt-1">
              {seller.store_id} - {seller.name}
            </span>
          )}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              API Provider
            </label>
            <div className="flex items-center gap-2">
              <select
                id="api-provider-select"
                className="form-select w-full"
                value={selectedProvider?.provider || ""}
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                <option value="" disabled>
                  Choose API
                </option>
                {providers.map((option) => (
                  <option key={option.provider} value={option.provider}>
                    {option.provider}
                  </option>
                ))}
              </select>
              {selectedProvider && (
                <SecondaryButton onClick={() => setSelectedProvider(null)}>
                  Clear
                </SecondaryButton>
              )}
            </div>
          </div>
        </div>

        {selectedProvider?.instructions && (
          <div className="mt-4 text-sm text-slate-600 dark:text-slate-300 italic p-3 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
            {selectedProvider.instructions}
          </div>
        )}
      </div>

      {selectedProvider && Object.keys(credentials).length > 0 && (
        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
          <h4 className="text-base font-medium text-slate-700 dark:text-slate-200 mb-4">
            Credentials
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {_.compact(renderCredentials(credentials))}
          </div>
        </div>
      )}

      {selectedProvider?.provider === "Lightspeed Retail" && (
        <div className="bg-slate-50 dark:bg-slate-800/30 p-4 rounded border border-slate-200 dark:border-slate-700 space-y-6">
          <h4 className="text-base font-medium text-slate-700 dark:text-slate-200">
            Lightspeed Account Management
          </h4>

          {credentials.account_master && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                  Account ID
                </label>
                <input
                  type="text"
                  className="form-input w-full"
                  value={credentials.account_id || ""}
                  disabled
                />
              </div>
              <div className="flex items-center">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked
                    disabled
                  />
                  Master Account
                </label>
              </div>
            </div>
          )}

          {!credentials.account_master && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-end">
                <PrimaryButton
                  onClick={fetchMasterTokens}
                  disabled={loadingMasterTokens}
                  loading={loadingMasterTokens}
                >
                  {loadingMasterTokens
                    ? "Loading..."
                    : "Fetch Master Accounts"}
                </PrimaryButton>
              </div>

              {masterTokens.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Select Master Account
                  </label>
                  <select
                    id="master-token-select"
                    className="form-select w-full"
                    value={selectedMasterToken?.account_id || ""}
                    onChange={(e) => handleMasterTokenSelect(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose master account
                    </option>
                    {masterTokens.map((token) => (
                      <option key={token.account_id} value={token.account_id}>
                        {`${token.store_id} - ${token.store_name}`} (ID:{" "}
                        {token.account_id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {credentials.account_id?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Sub-Account ID
                  </label>
                  <input
                    type="text"
                    className="form-input w-full"
                    value={credentials.account_id}
                    disabled
                  />
                </div>
              )}
            </div>
          )}

          <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
            <h4 className="text-base font-medium text-slate-700 dark:text-slate-200 mb-4">
              Lightspeed Shop Selection
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-end">
                <PrimaryButton
                  onClick={fetchShops}
                  disabled={loadingShops}
                  loading={loadingShops}
                >
                  {loadingShops ? "Loading..." : "Fetch Shops"}
                </PrimaryButton>
              </div>

              {lightspeedShops.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Select Shop
                  </label>
                  <select
                    id="lightspeed-shop-select"
                    className="form-select w-full"
                    value={selectedShop?.shopID || ""}
                    onChange={(e) => handleShopSelect(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose shop
                    </option>
                    {lightspeedShops.map((shop) => (
                      <option key={shop.shopID} value={shop.shopID}>
                        {shop.name} (ID: {shop.shopID})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <PrimaryButton
          onClick={handleTest}
          disabled={!selectedProvider || loading}
          loading={loading}
        >
          Test Credentials
        </PrimaryButton>
        {onCancel && (
          <SecondaryButton onClick={onCancel} disabled={loading}>
            Cancel
          </SecondaryButton>
        )}
        <PrimaryButton
          onClick={selectedProvider ? handleSubmit : handleRemove}
          disabled={loading}
          loading={loading}
        >
          {selectedProvider ? "Save" : "Remove Credentials"}
        </PrimaryButton>
      </div>
    </div>
  );
}

export default MarketplaceAPIDialog;
